'use client';

import { useState } from 'react';
import type { JSX } from 'react';
import { Button } from '../buttons/Button';
import { ChevronDown, ChevronRight, Plus, X, Shield, Lock, Database, Eye, Users } from 'lucide-react';
import { 
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectItem
} from './select';
import { useToast } from '../../hooks/useToast';
import type { RelationshipItem } from '../../interfaces/RelationshipItem';
import type { RelationshipConfig } from '../../interfaces/RelationshipConfig';
import type { ExpandableRelationshipCellProps } from '../../interfaces/ExpandableRelationshipCellProps';
import { Logger } from '@shared/logger';
import { UI_MESSAGES, UI_RESOURCES } from '@shared/constants';
import { FetchApiClient } from '@shared/httpService';
import { tryCatchAsync, err } from '@shared/utils';

// Generic badge component that adapts based on config
const RelationshipBadge = ({ 
  item,
  config,
  onRemove,
  isRemoving = false
}: { 
  item: RelationshipItem;
  config: RelationshipConfig;
  onRemove?: () => void;
  isRemoving?: boolean;
}) => {
  // Use custom component if provided
  if (config.customBadgeComponent) {
    const CustomComponent = config.customBadgeComponent;
    return <CustomComponent item={item} onRemove={onRemove} isRemoving={isRemoving} />;
  }

  // Default badge implementation
  const displayText = item[config.displayField] || 'Unknown';
  const description = config.descriptionField ? item[config.descriptionField] : undefined;
  
  // Get icon based on type or field
  const getIcon = () => {
    if (config.iconField) {
      const iconType = item[config.iconField]?.toLowerCase();
      switch (iconType) {
        case 'read': return <Eye className="w-3 h-3" />;
        case 'create': case 'update': case 'delete': return <Database className="w-3 h-3" />;
        case 'manage': return <Shield className="w-3 h-3" />;
        case 'admin': return <Shield className="w-3 h-3" />;
        case 'user': return <Users className="w-3 h-3" />;
        default: return <Lock className="w-3 h-3" />;
      }
    }
    // Default icons based on relationship type
    return config.relationshipType === 'many-to-many' ? <Shield className="w-3 h-3" /> : <Users className="w-3 h-3" />;
  };

  // Get color based on field or type
  const getColor = () => {
    if (config.colorField) {
      const colorValue = item[config.colorField]?.toLowerCase();
      switch (colorValue) {
        case 'admin': case 'manage': return 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100';
        case 'user': case 'create': return 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100';
        case 'guest': case 'read': return 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100';
        case 'delete': return 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100';
        case 'update': return 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100';
        default: return 'bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100';
      }
    }
    return 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100';
  };

  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${getColor()}`}>
      {getIcon()}
      <span className="font-medium">{displayText}</span>
      {description && (
        <span className="text-xs opacity-75">({description})</span>
      )}
      {onRemove && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          disabled={isRemoving}
          className="p-0 h-4 w-4 ml-1 rounded-full hover:bg-red-500 hover:text-white transition-colors duration-200 disabled:opacity-50"
        >
          {isRemoving ? (
            <div className="w-2 h-2 border border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <X className="h-3 w-3" />
          )}
        </Button>
      )}
    </div>
  );
};

export function ExpandableRelationshipCell({
  itemId,
  currentItems = [],
  availableItems = [],
  config,
  endpoint,
  onDataChange,
  isLoading: externalLoading = false,
  relatedData = {}
}: ExpandableRelationshipCellProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const [additionalFields, setAdditionalFields] = useState<Record<string, any>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [removingItemId, setRemovingItemId] = useState<string | null>(null);
  const [builderFormData, setBuilderFormData] = useState<Record<string, string>>({});
  const { toast } = useToast();
  const fetchApi = new FetchApiClient();

  // Get available items that aren't already assigned
  const unassignedItems = availableItems.filter(
    item => !currentItems.some(currentItem => 
      currentItem.id === item.id || 
      (currentItem[config.targetIdField as keyof RelationshipItem] as string) === item.id
    )
  );

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
    // Reset adding state when collapsing
    if (isExpanded) {
      resetAddForm();
    }
  };

  const resetAddForm = () => {
    setIsAddingItem(false);
    setSelectedItemId('');
    setAdditionalFields({});
    setBuilderFormData({});
  };

  const handleAddItem = async (): Promise<void> => {
    if (config.builderMode) {
      // Builder mode - validate all required fields
      const requiredFields = config.builderFields?.filter(field => field.required) || [];
      const missingFields = requiredFields.filter(field => !builderFormData[field.key]);
      
      if (missingFields.length > 0) {
        toast({
          title: "Error",
          description: `Please fill in all required fields: ${missingFields.map(f => f.label).join(', ')}`,
          variant: "destructive",
        });
        return;
      }
    } else {
      // Standard mode - validate selected item
      if (!selectedItemId) return;
    }

    setIsLoading(true);
    const result = await tryCatchAsync(async () => {
      const payload = {
        action: config.addAction,
        ...(config.builderMode ? builderFormData : { [config.targetIdField]: selectedItemId }),
        ...additionalFields
      };

      const apiResult = await fetchApi.put(`${endpoint}/${itemId}`, payload);

      if (apiResult.isErr) {
        Logger.error('ExpandableRelationshipCell: API call failed', {
          resource: UI_RESOURCES.DASHBOARD,
          error: apiResult.getFail()
        });
        return err(apiResult.getFail());
      }

      const apiResponse = apiResult.getSuccess();
      if (!apiResponse.ok) {
        Logger.error('ExpandableRelationshipCell: API response not OK', {
          resource: UI_RESOURCES.DASHBOARD,
          status: apiResponse.status,
          data: apiResponse.data
        });
        return err(new Error(`Failed to add ${config.displayField}`));
      }

      const result = apiResponse.data as any;
      
      let updatedItems;
      if (config.relationshipType === 'many-to-many') {
        const targetId = result[config.targetIdField] || result[`${config.targetIdField.replace(/Id$/, '_id')}`] || selectedItemId;
        const addedItem = availableItems.find(item => item.id === targetId);
        
        if (addedItem) {
          updatedItems = [...currentItems, addedItem];
        } else {
          Logger.warn('ExpandableRelationshipCell: Added item not found in availableItems', {
            resource: UI_RESOURCES.DASHBOARD,
            targetId,
            availableItemsIds: availableItems.map(item => item.id)
          });
          updatedItems = [...currentItems, result];
        }
      } else {
        const addedItem = availableItems.find(item => item.id === selectedItemId);
        if (addedItem) {
          updatedItems = [...currentItems, addedItem];
        }
      }
      
      if (updatedItems) {
        onDataChange?.(updatedItems);
      }
      
      resetAddForm();
      
      const addedItem = availableItems.find(item => item.id === selectedItemId);
      const itemName = addedItem?.[config.displayField] || 'item';
      
      toast({
        title: 'Item Added',
        description: `Successfully added ${itemName}`,
      });
    });

    if (result.isErr) {
      const error = result.getFail();
      Logger.error(UI_MESSAGES.ERROR.ADD_ITEM_ERROR, { resource: UI_RESOURCES.DASHBOARD }, error instanceof Error ? error : new Error(String(error)));
      toast({
        title: UI_MESSAGES.ERROR.GENERAL_ERROR,
        description: `Failed to add ${config.displayField}. Please try again.`,
        variant: 'destructive',
      });
    }
    
    setIsLoading(false);
  };

  const handleRemoveItem = async (item: RelationshipItem): Promise<void> => {
    const itemToRemoveId = config.relationshipType === 'many-to-many' ? item.id : (item[config.targetIdField as keyof RelationshipItem] as string) || item.id;
    
    setRemovingItemId(itemToRemoveId);
    const result = await tryCatchAsync(async () => {
      const payload = {
        action: config.removeAction,
        [config.targetIdField]: itemToRemoveId
      };

      const apiResult = await fetchApi.put(`${endpoint}/${itemId}`, payload);

      if (apiResult.isErr) {
        return err(apiResult.getFail());
      }

      const apiResponse = apiResult.getSuccess();
      if (!apiResponse.ok) {
        return err(new Error(`Failed to remove ${config.displayField}`));
      }

      const updatedItems = currentItems.filter(currentItem => 
        config.relationshipType === 'many-to-many' 
          ? currentItem.id !== itemToRemoveId
          : ((currentItem[config.targetIdField as keyof RelationshipItem] as string) || currentItem.id) !== itemToRemoveId
      );
      
      onDataChange?.(updatedItems);
      
      const itemName = item[config.displayField] || 'item';
      toast({
        title: 'Item Removed',
        description: `Successfully removed ${itemName}`,
      });
    });

    if (result.isErr) {
      const error = result.getFail();
      Logger.error(UI_MESSAGES.ERROR.REMOVE_ITEM_ERROR, { resource: UI_RESOURCES.DASHBOARD }, error instanceof Error ? error : new Error(String(error)));
      toast({
        title: UI_MESSAGES.ERROR.GENERAL_ERROR,
        description: `Failed to remove ${config.displayField}. Please try again.`,
        variant: 'destructive',
      });
    }
    
    setRemovingItemId(null);
  };

  // Group items if grouping is configured
  const groupedItems = config.groupByField 
    ? currentItems.reduce((acc, item) => {
        const groupKey = (item[config.groupByField as keyof RelationshipItem] as string) || 'Other';
        if (!acc[groupKey]) acc[groupKey] = [];
        acc[groupKey].push(item);
        return acc;
      }, {} as Record<string, RelationshipItem[]>)
    : { 'All': currentItems };

  const canAddItem = config.builderMode 
    ? Object.keys(builderFormData).length > 0 && !isLoading && !externalLoading
    : selectedItemId && !isLoading && !externalLoading;
  const relationshipLabel = config.relationshipLabel || config.displayField.replace(/([A-Z])/g, ' $1').toLowerCase();

  return (
    <div className="space-y-3 min-w-0">
      {/* Header row with expand button and summary */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleToggleExpand}
          className="p-1.5 h-auto hover:bg-gray-100 rounded-md transition-colors duration-200"
          aria-label={isExpanded ? `Collapse ${relationshipLabel}s` : `Expand ${relationshipLabel}s`}
        >
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-gray-600" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-600" />
          )}
        </Button>
        
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {config.relationshipType === 'many-to-many' ? (
            <Shield className="h-4 w-4 text-gray-500 flex-shrink-0" />
          ) : (
            <Users className="h-4 w-4 text-gray-500 flex-shrink-0" />
          )}
          <span className="text-sm font-medium text-gray-900">
            {currentItems.length === 0 ? `No ${relationshipLabel}s` : 
             currentItems.length === 1 ? `1 ${relationshipLabel}` : 
             `${currentItems.length} ${relationshipLabel}s`}
          </span>
          
          {/* Preview badges when collapsed */}
          {currentItems.length > 0 && !isExpanded && (
            <div className="flex gap-1 flex-wrap overflow-hidden">
              {currentItems.slice(0, 2).map(item => (
                <RelationshipBadge 
                  key={item.id} 
                  item={item} 
                  config={config}
                />
              ))}
              {currentItems.length > 2 && (
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  +{currentItems.length - 2} more
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="ml-8 space-y-4">
          {/* Current items grouped */}
          {Object.keys(groupedItems).length > 0 && currentItems.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700">
                Current {relationshipLabel.charAt(0).toUpperCase() + relationshipLabel.slice(1)}s
              </h4>
              {Object.entries(groupedItems).map(([groupName, items]) => (
                <div key={groupName} className="space-y-2">
                  {config.groupByField && Object.keys(groupedItems).length > 1 && (
                    <h5 className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                      {groupName.replace(/^db_/, '').replace(/_/g, ' ')}
                    </h5>
                  )}
                  <div className={`flex flex-wrap gap-2 ${config.groupByField && Object.keys(groupedItems).length > 1 ? 'ml-4' : ''}`}>
                    {items.map(item => (
                      <RelationshipBadge 
                        key={item.id}
                        item={item}
                        config={config}
                        onRemove={() => handleRemoveItem(item)}
                        isRemoving={removingItemId === (config.relationshipType === 'many-to-many' ? item.id : (item[config.targetIdField as keyof RelationshipItem] as string) || item.id)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add item section */}
          {(config.builderMode || unassignedItems.length > 0) && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-700">
                  Add {relationshipLabel.charAt(0).toUpperCase() + relationshipLabel.slice(1)}
                </h4>
                {currentItems.length === 0 && (
                  <span className="text-xs text-gray-500">
                    No {relationshipLabel}s assigned
                  </span>
                )}
              </div>
              
              {!isAddingItem ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAddingItem(true)}
                  className="text-sm border-dashed border-2 border-gray-300 hover:border-blue-400 hover:bg-blue-50 transition-colors duration-200"
                  disabled={externalLoading}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add {relationshipLabel.charAt(0).toUpperCase() + relationshipLabel.slice(1)}
                </Button>
              ) : (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg border">
                  {config.builderMode ? (
                    // Builder form with multiple fields
                    <div className="space-y-4">
                      {config.builderFields?.map(field => {
                        const fieldData = relatedData[field.dataKey] || [];
                        return (
                          <div key={field.key} className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">
                              {field.label}
                              {field.required && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            <Select 
                              value={builderFormData[field.key] || ''} 
                              onValueChange={(value) => setBuilderFormData(prev => ({ ...prev, [field.key]: value }))}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={field.placeholder} />
                              </SelectTrigger>
                              <SelectContent>
                                {fieldData.map(item => (
                                  <SelectItem 
                                    key={field.getValue ? field.getValue(item) : item.id} 
                                    value={field.getValue ? field.getValue(item) : item.id}
                                  >
                                    <div className="flex items-center gap-2">
                                      <Shield className="w-3 h-3" />
                                      <span>{field.getDisplayValue ? field.getDisplayValue(item) : item[config.displayField]}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    // Standard dropdown for selecting existing items
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">
                        Select {relationshipLabel.charAt(0).toUpperCase() + relationshipLabel.slice(1)}
                      </label>
                      <Select value={selectedItemId} onValueChange={setSelectedItemId}>
                        <SelectTrigger>
                          <SelectValue placeholder={`Choose ${relationshipLabel} to assign...`} />
                        </SelectTrigger>
                        <SelectContent>
                          {unassignedItems.map(item => (
                            <SelectItem key={item.id} value={item.id}>
                              <div className="flex items-center gap-2">
                                <Shield className="w-3 h-3" />
                                <span>{item[config.displayField]}</span>
                                {config.descriptionField && item[config.descriptionField] && (
                                  <span className="text-xs text-gray-500">
                                    - {item[config.descriptionField]}
                                  </span>
                                )}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleAddItem}
                      disabled={!canAddItem}
                      className="flex-1"
                    >
                      {isLoading ? (
                        <>
                          <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin mr-2" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <Plus className="h-3 w-3 mr-2" />
                          Add {relationshipLabel.charAt(0).toUpperCase() + relationshipLabel.slice(1)}
                        </>
                      )}
                    </Button>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={resetAddForm}
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* No available items message - only show for non-builder mode */}
          {!config.builderMode && unassignedItems.length === 0 && currentItems.length > 0 && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                All available {relationshipLabel}s have been assigned.
              </p>
            </div>
          )}

          {/* No items at all message - only show for non-builder mode */}
          {!config.builderMode && currentItems.length === 0 && unassignedItems.length === 0 && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-600">
                No {relationshipLabel}s available to assign.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}